// Serviço para notificações de transferências no Firestore.
// Collection: 'transferNotifications'
// Ver docs/NOTIFICACOES-FIRESTORE.md para estrutura e fluxo.

import {
  collection,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';

const NOTIFICATIONS_COLLECTION = 'transferNotifications';

function normalizeAddress(address) {
  if (!address || typeof address !== 'string') return '';
  return address.trim().toLowerCase();
}

/**
 * Cria uma notificação de transferência no Firestore.
 * Chamada pelo remetente após confirmação da transação.
 * @param {Object} data - from, to, txHash, amount, token, tokenAddress, network, networkName?, sentBy?
 * @returns {Promise<string>} ID do documento criado
 */
export async function createTransferNotification(data) {
  if (!db) throw new Error('Firestore não inicializado');

  if (!data?.from || !data?.to || !data?.txHash) {
    throw new Error('Dados incompletos: from, to e txHash são obrigatórios');
  }
  if (!data?.amount || !data?.token || !data?.network) {
    throw new Error('Dados incompletos: amount, token e network são obrigatórios');
  }

  const payload = {
    from: normalizeAddress(data.from),
    to: normalizeAddress(data.to),
    txHash: data.txHash,
    amount: String(data.amount),
    token: data.token,
    tokenAddress: normalizeAddress(data.tokenAddress ?? ''),
    network: data.network,
    networkName: data.networkName ?? '',
    sentBy: data.sentBy ?? '',
    sentAt: serverTimestamp(),
    notifiedAt: null,
    verifiedAt: null,
    status: 'pending',
  };

  const docRef = await addDoc(collection(db, NOTIFICATIONS_COLLECTION), payload);
  return docRef.id;
}

/**
 * Atualiza o status de uma notificação.
 * @param {string} notificationId - ID do documento
 * @param {string} status - 'pending' | 'verified' | 'read' | 'expired'
 */
export async function updateNotificationStatus(notificationId, status) {
  if (!db) throw new Error('Firestore não inicializado');
  if (!notificationId) throw new Error('notificationId é obrigatório');

  const valid = ['pending', 'verified', 'read', 'expired'];
  if (!valid.includes(status)) throw new Error(`Status inválido: ${status}`);

  const ref = doc(db, NOTIFICATIONS_COLLECTION, notificationId);
  const updateData = { status };
  if (status === 'verified') updateData.verifiedAt = serverTimestamp();
  if (status === 'read') updateData.notifiedAt = serverTimestamp();

  await updateDoc(ref, updateData);
}

/**
 * Inscreve para notificações de um destinatário (real-time).
 * @param {string} walletAddress - Endereço do destinatário
 * @param {Function} onNotification - Callback(notification) para cada nova notificação
 * @returns {Function} unsubscribe
 */
export function subscribeToTransferNotifications(walletAddress, onNotification) {
  if (!db || !walletAddress) return () => {};

  const to = normalizeAddress(walletAddress);
  const q = query(
    collection(db, NOTIFICATIONS_COLLECTION),
    where('to', '==', to),
    where('status', '==', 'pending'),
    orderBy('sentAt', 'desc'),
    limit(20)
  );

  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          onNotification({ id: change.doc.id, ...change.doc.data() });
        }
      });
    },
    (err) => console.warn('[TransferNotificationService] Listener error:', err)
  );

  return unsubscribe;
}
